import * as React from "react";
import classnames from "classnames";
import { ScrollIntoView, Link, Button } from "@grandvisionhq/common";
import { Heading, Divider } from "@grandvisionhq/elements";
import { Form, Email, Password } from "@grandvisionhq/forms";
import { useLink } from "@grandvisionhq/frk-routing";
import { Alert } from "@grandvisionhq/iris-ui";
import { connect, useIntl, statusFailed, statusLoading } from "@grandvisionhq/state";
import { useTealiumEventProperties, useTrackPageView } from "@grandvisionhq/svc-tealium/events";
import { SignUpUsp } from "../../components/SignUpUsp/components/SignUpUsp/SignUpUsp.js";
import { SocialLogin } from "../../components/SocialLogin/components/SocialLogin/SocialLogin.js";
import { useAdditionalLoginProviders } from "../../hooks/hooks/use-additional-login-providers.js";
import { useSocialLoginFlow } from "../../hooks/hooks/use-social-login-flow.js";
import { resetPassword } from "../../ResetPassword/ResetPassword/actions.js";
import { getFormattedRoutes, getAuth } from "../../selectors.js";
import { loginWithPassword } from "./actions.js";
/* empty css           */
const LABEL_PREFIX = "account.login";
const ERROR_PREFIX = `${LABEL_PREFIX}.errors`;
const NonCompliantPasswordMessage = (props) => {
  const { href: resetPasswordRoute } = useLink({ route: "auth.reset-password" }) || {};
  const { getLabel } = useIntl();
  const label = getLabel(`${ERROR_PREFIX}.requiresPasswordChange`);
  const handleClick = React.useCallback(() => {
    props.resetPassword({ email: props.email || "" });
  }, [props.resetPassword, props.email]);
  const regex = /(.+)\[(.+)\](.+)/g;
  const linkElement = props.email ? /* @__PURE__ */ React.createElement(
    "button",
    {
      className: "login__reset-link",
      onClick: handleClick,
      "data-t": "non-compliant-password-action"
    },
    label.replace(regex, "$2")
  ) : /* @__PURE__ */ React.createElement(
    "a",
    {
      className: "login__reset-link",
      href: resetPasswordRoute || "#",
      "data-t": "non-compliant-password-action"
    },
    label.replace(regex, "$2")
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", null, label.replace(regex, "$1")), linkElement, /* @__PURE__ */ React.createElement("span", null, label.replace(regex, "$3")));
};
NonCompliantPasswordMessage.displayName = "NonCompliantPasswordMessage";
const initialFieldState = {
  isTouched: false,
  isValid: false,
  value: ""
};
const Login = ({
  auth,
  email,
  error,
  className,
  loginWithPassword: loginWithPassword2,
  disableSignup,
  showSocialLogin = true,
  socialLoginButtonLabel,
  returnPath,
  resetPassword: resetPassword2,
  showTitle = true
}) => {
  const { hasProviders } = useAdditionalLoginProviders();
  const { getLabel, hasLabel } = useIntl();
  const shouldRenderSignUpUsp = hasLabel("account.signup.usp.noAccountYet");
  const { href: resetPasswordRoute } = useLink({ route: "auth.reset-password" }) || {};
  const { href: signUpRoute } = useLink({ route: "auth.signup" }) || {};
  const [hasFormValidationError, setHasFormValidationError] = React.useState(false);
  const [loginForm, setLoginForm] = React.useState({
    email: {
      name: "email",
      ...initialFieldState
    },
    password: {
      name: "password",
      ...initialFieldState
    }
  });
  const { initializeSocialLoginFlow } = useSocialLoginFlow();
  const formattedError = React.useMemo(() => {
    if (statusFailed(auth.loginCall)) {
      const { code, error_description, message } = auth.loginCall.error;
      if (code === "access_denied") {
        return getLabel(`${ERROR_PREFIX}.invalidCredentials`);
      }
      if (code === "password_leaked") {
        return getLabel(`${ERROR_PREFIX}.passwordLeaked`);
      }
      return error_description || message || "";
    }
    if (error === "requiresPasswordChange") {
      return;
    }
    if (typeof error === "string") {
      return getLabel(`${ERROR_PREFIX}.${error}`);
    }
    if (hasFormValidationError) {
      return getLabel(`${ERROR_PREFIX}.formValidation`);
    }
  }, [error, auth.loginCall, getLabel, hasFormValidationError]);
  const handleSubmit = React.useCallback(
    (state) => {
      const { formState } = state;
      if (formState?.email?.isValid && formState?.password?.isValid) {
        setHasFormValidationError(false);
        loginWithPassword2({
          email: formState.email.value,
          password: formState.password.value,
          returnPath
        });
      }
      if (!formState?.email?.isTouched || !formState?.password?.isTouched) {
        return setLoginForm({
          email: {
            ...formState.email,
            isTouched: true
          },
          password: {
            ...formState.password,
            isTouched: true
          }
        });
      }
      setHasFormValidationError(true);
    },
    [loginWithPassword2, returnPath, setHasFormValidationError, setLoginForm]
  );
  const wrapperClasses = classnames("login", className);
  const isLoading = statusLoading(auth.loginCall);
  const { commonEventProperties, getCommonUserProperties } = useTealiumEventProperties();
  const loginPageView = React.useCallback(async () => {
    const commonUserProperties = await getCommonUserProperties();
    if (!commonUserProperties) return;
    return {
      id: "VirtualPage-View",
      Page_Section1: "Account",
      Page_Type: "Login&Register",
      Page_Design: "Login",
      ...commonEventProperties,
      ...commonUserProperties
    };
  }, [commonEventProperties, getCommonUserProperties]);
  useTrackPageView(loginPageView);
  return /* @__PURE__ */ React.createElement("div", { className: wrapperClasses }, /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("section", { className: "login__section" }, showTitle && /* @__PURE__ */ React.createElement(Heading, { variant: hasProviders ? "small" : "medium", className: "login__section-title" }, getLabel(`${LABEL_PREFIX}.title`)), showSocialLogin && hasProviders && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    SocialLogin,
    {
      onLogin: initializeSocialLoginFlow,
      returnPath,
      labels: {
        googleButton: getLabel(socialLoginButtonLabel || "account.login.social.google"),
        consent: getLabel(`${LABEL_PREFIX}.social.consent`)
      }
    }
  ), /* @__PURE__ */ React.createElement(Divider, { className: "login__divider", caption: getLabel("account.signup.general.or") }), /* @__PURE__ */ React.createElement(Heading, { variant: "smallest", className: "login__section-title" }, getLabel(`${LABEL_PREFIX}.title.emailLogin`))), !isLoading && formattedError && /* @__PURE__ */ React.createElement(ScrollIntoView, { key: formattedError }, /* @__PURE__ */ React.createElement(Alert, { variant: "error", className: "alert-m-default" }, formattedError)), error === "requiresPasswordChange" && /* @__PURE__ */ React.createElement(ScrollIntoView, { key: error }, /* @__PURE__ */ React.createElement(Alert, { variant: "warning", className: "alert-m-default login__non-compliancy-alert" }, /* @__PURE__ */ React.createElement(NonCompliantPasswordMessage, { resetPassword: resetPassword2, email }))), /* @__PURE__ */ React.createElement(
    Form,
    {
      onSubmit: handleSubmit,
      initialFormState: loginForm,
      name: "login",
      "data-t": "login-form",
      showErrorsOnInvalidSubmit: false
    },
    /* @__PURE__ */ React.createElement(
      Email,
      {
        "data-t": "email",
        required: true,
        label: getLabel("fields.input.email.label"),
        errorMessage: getLabel("fields.input.email.errorMessage"),
        hideAsteriskSign: true
      }
    ),
    /* @__PURE__ */ React.createElement(
      Password,
      {
        "data-t": "password",
        required: true,
        label: getLabel("fields.input.password.label"),
        errorMessage: getLabel("fields.input.password.errorMessage"),
        hideAsteriskSign: true
      }
    ),
    /* @__PURE__ */ React.createElement(
      Link,
      {
        className: "login__forgot-password",
        label: getLabel("account.action.resetPassword"),
        url: resetPasswordRoute || "#"
      }
    ),
    /* @__PURE__ */ React.createElement(
      Button,
      {
        "data-t": "submit",
        type: "submit",
        isLoading,
        caption: getLabel("account.action.login"),
        className: "login__button"
      }
    )
  )), !disableSignup && /* @__PURE__ */ React.createElement("section", { className: "login__section" }, shouldRenderSignUpUsp && /* @__PURE__ */ React.createElement(SignUpUsp, null), /* @__PURE__ */ React.createElement(
    Button,
    {
      "data-t": "create-account",
      variant: "outline",
      caption: getLabel("account.action.createAccount"),
      className: "login__button login__button--signup",
      href: signUpRoute || "#"
    }
  ))));
};
const mapStateToProps = (state) => ({
  auth: getAuth(state),
  routes: getFormattedRoutes(state)
});
const mapDispatchToProps = {
  loginWithPassword,
  resetPassword
};
const Login$1 = connect(mapStateToProps, mapDispatchToProps)(Login);
export {
  ERROR_PREFIX,
  LABEL_PREFIX,
  Login,
  NonCompliantPasswordMessage,
  Login$1 as default,
  mapDispatchToProps,
  mapStateToProps
};
//# sourceMappingURL=Login.js.map
